// Lagertelling Service - Bruker eksisterende StcTransOrder endepunkter
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface InventoryCountRequest {
  itemNo: string;
  locationName: string;
  quantity: number;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  description1?: string;
  warehouse: string;
}

export interface InventoryCountLine {
  lineID: number;
  itemNo: string;
  locationName: string;
  quantity: number;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  description1: string;
  warehouse: string;
  transDt: Date;
  lineStatus: number;
  statusText: string;
}

export interface InventorySession {
  batchID: string;
  operatorID: string;
  description1: string;
  orderStatus: number;
  statusText: string;
  warehouse: string;
  orderDate: Date;
  transactionCount: number;
  lines: InventoryCountLine[];
}

export interface ItemValidationResult {
  valid: boolean;
  itemNo: string;
  description1?: string;
  itemType?: number;
  errorMessage?: string;
}

export interface LocationValidationResult {
  valid: boolean;
  locationName: string;
  warehouseID?: string;
  errorMessage?: string;
}

export interface InventoryFormData {
  scannedCode: string;
  quantity: string;
  isScanning: boolean;
  validationMessage: string;
  validationType: 'success' | 'error' | '';
}

/**
 * Response from completing an inventory counting session.
 * Contains information about whether the count was exported directly
 * or requires manager validation.
 */
export interface CompleteCountingResponse {
  success: boolean;
  /**
   * Whether the counting requires manager validation before export.
   * When true, the count is saved but not exported - it goes to the manager dashboard for review.
   * When false, the count was exported directly to ERP.
   */
  requiresValidation: boolean;
  /** Message to display to the operator */
  message: string;
  /** Batch ID of the completed counting session */
  batchId?: string;
  /** Current order status after completion (40 = Awaiting validation, 50 = Completed, 60 = Exported) */
  orderStatus: number;
}

// Interface for lagerbeholdning på lokasjoner
export interface ItemLocationInfo {
  itemNo: string;
  description: string;
  locationName: string;
  warehouseID: string;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
}

class InventoryService {
  private baseUrl = API_BASE_URL;

  // Få aktiv tellesession for operatør (top 1)
  async getActiveInventorySession(operatorId: string): Promise<InventorySession | null> {
    try {
      // Hent alle counting sessions for operatøren (OrderType 1 = counting, Status 20 = Under arbeid)
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/operator/${operatorId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch inventory sessions');
      }
      const sessions = await response.json();
      
      // Finn aktiv counting session (status 20 og OrderType 1)
      const activeSessions = sessions.filter((s: any) => 
        s.orderType === 1 && s.orderStatus === 20
      );
      
      if (activeSessions.length === 0) {
        return null;
      }

      // Ta den nyeste
      const activeSession = activeSessions.sort((a: any, b: any) => 
        new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
      )[0];

      // Hent detaljer med transakcsjoner
      const detailResponse = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${activeSession.batchID}/details`);
      if (!detailResponse.ok) {
        throw new Error('Failed to fetch session details');
      }
      const details = await detailResponse.json();

      return this.mapToInventorySession(details);
    } catch (error) {
      console.error('Error getting active inventory session:', error);
      return null;
    }
  }

  // Opprett ny tellesession
  async createInventorySession(operatorId: string, warehouse: string): Promise<InventorySession> {
    try {
      const now = new Date();
      const batchID = `${now.toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14)} ${operatorId}`;
      
      const request = {
        batchID,
        operatorID: operatorId,
        orderDate: now,
        description1: `${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')} ${operatorId}`,
        orderStatus: 20, // Under arbeid
        profileID: `20000477${operatorId}`, // Standard profil
        orderType: 1, // Counting
        warehouse,
        lineTypeOrigin: 1
        // StatID genereres automatisk av backend (erstatter INSERT trigger)
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to create inventory session');
      }
      
      // Return session med tomme linjer
      const responseData = await response.json();
      return {
        batchID: responseData.batchID,
        operatorID: responseData.operatorID,
        description1: responseData.description1,
        orderStatus: responseData.orderStatus,
        statusText: responseData.statusText,
        warehouse: responseData.warehouse,
        orderDate: new Date(responseData.orderDate),
        transactionCount: 0,
        lines: []
      };
    } catch (error) {
      console.error('Error creating inventory session:', error);
      throw new Error('Kunne ikke opprette tellesession');
    }
  }

  // Valider varenummer
  async validateItem(itemNo: string): Promise<ItemValidationResult> {
    try {
      console.log('🔍 [validateItem] Validating item:', itemNo);
      const startTime = performance.now();

      // Try barcode search first
      const barcodeResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/barcode/current?barcode=${encodeURIComponent(itemNo)}`);

      const barcodeEndTime = performance.now();
      console.log(`⏱️ [validateItem] Barcode search took ${Math.round(barcodeEndTime - startTime)}ms, status: ${barcodeResponse.status}`);

      if (barcodeResponse.ok) {
        const barcodeData = await barcodeResponse.json();
        if (barcodeData && barcodeData.length > 0) {
          // Only accept exact barcode match — partial LIKE matches should fall through
          // to item search which has serial/batch priority ordering
          const exactBarcodeMatch = barcodeData.find((i: any) =>
            i.itemBarCode && i.itemBarCode.toLowerCase() === itemNo.toLowerCase()
          );
          if (exactBarcodeMatch) {
            console.log('✅ [validateItem] Item found via exact barcode:', exactBarcodeMatch.itemNo);
            return {
              valid: true,
              itemNo: exactBarcodeMatch.itemNo,
              description1: exactBarcodeMatch.description1,
              itemType: exactBarcodeMatch.itemType
            };
          }
          console.log('🔍 [validateItem] Barcode LIKE match found but no exact match, continuing to item search...');
        }
      }

      // Barcode didn't match - try direct item number search using OPTIMIZED search endpoint
      console.log('🔍 [validateItem] Barcode not found, trying direct item number search...');
      const itemSearchStart = performance.now();
      const itemResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/search/current?searchTerm=${encodeURIComponent(itemNo)}`);

      const itemSearchEnd = performance.now();
      console.log(`⏱️ [validateItem] Item search took ${Math.round(itemSearchEnd - itemSearchStart)}ms, status: ${itemResponse.status}`);

      if (!itemResponse.ok) {
        return {
          valid: false,
          itemNo,
          errorMessage: `Vare "${itemNo}" ble ikke funnet`
        };
      }

      const itemData = await itemResponse.json();
      if (itemData && itemData.length > 0) {
        // Find exact match if exists, otherwise take first result
        const exactMatch = itemData.find((i: any) => i.itemNo.toLowerCase() === itemNo.toLowerCase());
        const item = exactMatch || itemData[0];
        console.log('✅ [validateItem] Item found via item search:', item.itemNo);
        return {
          valid: true,
          itemNo: item.itemNo,
          description1: item.description1,
          itemType: item.itemType
        };
      }

      console.log('❌ [validateItem] Item not found');
      return {
        valid: false,
        itemNo,
        errorMessage: `Vare "${itemNo}" ble ikke funnet`
      };
    } catch (error) {
      console.error('❌ [validateItem] Error validating item:', error);
      return {
        valid: false,
        itemNo,
        errorMessage: 'Feil ved validering av vare'
      };
    }
  }

  // Valider lokasjon
  async validateLocation(locationName: string): Promise<LocationValidationResult> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stc-bins/current`);
      if (!response.ok) {
        throw new Error('Failed to validate location');
      }
      const bins = await response.json();
      
      const foundBin = bins.find((bin: any) => bin.binCode === locationName);
      
      if (foundBin) {
        return {
          valid: true,
          locationName: foundBin.binCode,
          warehouseID: foundBin.warehouseID
        };
      }

      return {
        valid: false,
        locationName,
        errorMessage: `Lokasjon "${locationName}" ble ikke funnet`
      };
    } catch (error) {
      console.error('Error validating location:', error);
      return {
        valid: false,
        locationName,
        errorMessage: 'Feil ved validering av lokasjon'
      };
    }
  }

  // Legg til tellelinje
  async addCountLine(batchId: string, countData: InventoryCountRequest): Promise<InventoryCountLine> {
    try {
      // Hent operatorID fra batchID (format: "YYYYMMDDHHMMSS OperatorID")
      const operatorID = batchId.split(' ').slice(1).join(' ');
      
      const request = {
        batchID: batchId,
        lineType: 1,
        orderNo: 0,
        lineNo: 0,
        locationName: countData.locationName,
        quantity: countData.quantity,
        transDt: new Date(),
        toLocationName: '',
        warehouse: countData.warehouse,
        toWarehouse: '',
        operatorID: operatorID,
        profileID: `20000477${operatorID}`, // Samme som ved opprettelse av session
        exportedYesNo: false,
        lineStatus: 40, // Behandlet, komplett
        itemNo: countData.itemNo,
        itemUnitCode: '',
        itemBarCode: null,
        description1: countData.description1 || '',
        itemBatchNo: countData.itemBatchNo || '',
        itemBatchExpDate: countData.itemBatchExpDate,
        itemSerialNo: countData.itemSerialNo || '',
        erpLinkName: '',
        shipmentNo: '',
        quantityOrigin: 0,
        itemUnitTxt: '',
        lineTypeOrigin: 1,
        price: 0,
        currency: '',
        reasonCode: 0,
        transType: 4, // Telling
        sscc: '',
        quantityCorrected: null,
        freeField1: null,
        freeField2: null,
        freeField3: null,
        appFreeField1: null,
        appFreeField2: null,
        quantityGross: 0,
        transitPackagingID: 0,
        stcBoxNo: 0,
        grShipmentNo: 0,
        externalSystemID: null,
        externalSystemStatus: null,
        itemType: 0,
        orderNoERP: null,
        lineNoERP: 0
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/transactions`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to add count line');
      }
      
      const responseData = await response.json();
      return this.mapToInventoryCountLine(responseData);
    } catch (error) {
      console.error('Error adding count line:', error);
      throw new Error('Kunne ikke legge til tellelinje');
    }
  }

  // Hent tellelinjer for session
  async getInventoryLines(batchId: string): Promise<InventoryCountLine[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${batchId}/transactions`);
      if (!response.ok) {
        throw new Error('Failed to fetch inventory lines');
      }
      const transactions = await response.json();
      
      return transactions
        .filter((t: any) => t.transType === 4) // Kun telling
        .map((t: any) => this.mapToInventoryCountLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime());
    } catch (error) {
      console.error('Error getting inventory lines:', error);
      return [];
    }
  }

  // Fullfør tellesession
  async completeInventorySession(batchId: string): Promise<CompleteCountingResponse> {
    try {
      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/complete-counting`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        return {
          success: false,
          requiresValidation: false,
          message: errorData?.message || 'Kunne ikke fullføre tellesession',
          batchId,
          orderStatus: 0
        };
      }
      const data = await response.json();
      return {
        success: data.success,
        requiresValidation: data.requiresValidation,
        message: data.message,
        batchId: data.batchId,
        orderStatus: data.orderStatus
      };
    } catch (error) {
      console.error('Error completing inventory session:', error);
      return {
        success: false,
        requiresValidation: false,
        message: 'Feil ved fullføring av tellesession',
        batchId,
        orderStatus: 0
      };
    }
  }

  // Hent lokasjoner for en vare (med lagerbeholdning)
  async getLocationsForItem(itemNo: string): Promise<ItemLocationInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`);
      if (!response.ok) {
        throw new Error('Failed to get locations for item');
      }

      const itemBins = await response.json();

      return itemBins.map((bin: any): ItemLocationInfo => ({
        itemNo: bin.itemNo,
        description: bin.itemDescription || '',
        locationName: bin.binCode,
        warehouseID: bin.warehouseID,
        availableQuantity: bin.quantityAvailable || 0,
        totalQuantity: bin.quantity || 0,
        reservedQuantity: bin.quantityReserved || 0
      })).filter((location: ItemLocationInfo) => location.availableQuantity > 0);
    } catch (error) {
      console.error('Error getting locations for item:', error);
      return [];
    }
  }

  // Oppdater tellelinje
  async updateCountLine(batchId: string, lineId: number, quantity: number): Promise<InventoryCountLine> {
    try {
      const request = {
        quantity: quantity
      };

      const response = await AuthService.authenticatedPut(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to update count line');
      }
      
      const responseData = await response.json();
      return this.mapToInventoryCountLine(responseData);
    } catch (error) {
      console.error('Error updating count line:', error);
      throw new Error('Kunne ikke oppdatere tellelinje');
    }
  }

  // Slett tellelinje
  async deleteCountLine(batchId: string, lineId: number): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedDelete(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to delete count line');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting count line:', error);
      throw new Error('Kunne ikke slette tellelinje');
    }
  }

  // Helper methods
  private mapToInventorySession(details: any): InventorySession {
    return {
      batchID: details.header.batchID,
      operatorID: details.header.operatorID,
      description1: details.header.description1,
      orderStatus: details.header.orderStatus,
      statusText: details.header.statusText,
      warehouse: details.header.warehouse,
      orderDate: new Date(details.header.orderDate),
      transactionCount: details.totalTransactions,
      lines: details.transactions
        .filter((t: any) => t.transType === 4) // Kun telling
        .map((t: any) => this.mapToInventoryCountLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime())
    };
  }

  private mapToInventoryCountLine(transaction: any): InventoryCountLine {
    return {
      lineID: transaction.lineID,
      itemNo: transaction.itemNo,
      locationName: transaction.locationName,
      quantity: transaction.quantity,
      itemBatchNo: transaction.itemBatchNo,
      itemBatchExpDate: transaction.itemBatchExpDate ? new Date(transaction.itemBatchExpDate) : undefined,
      itemSerialNo: transaction.itemSerialNo,
      description1: transaction.description1,
      warehouse: transaction.warehouse,
      transDt: new Date(transaction.transDt),
      lineStatus: transaction.lineStatus,
      statusText: transaction.statusText || 'Behandlet'
    };
  }
}

export const inventoryService = new InventoryService();
